import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { cn } from '@/lib/utils';
import {
    useFirstCaptureChoice,
    useFirstCaptureConversion,
    type FirstCaptureChoicePoint,
    type FirstCaptureConversionPoint,
    type IncomeGranularity,
} from '@/hooks/useAdminAnalytics';

const LINE_COLOR = 'hsl(142, 71%, 45%)';
const SECRET_COLOR = 'hsl(199, 89%, 58%)';
const PUBLIC_COLOR = 'hsl(168, 76%, 46%)';

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

const GRANULARITY_OPTIONS: { value: IncomeGranularity; label: string }[] = [
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
];

const NOTE =
    'De los usuarios registrados en cada periodo, porcentaje que ha publicado al menos una captura (sin borradores), contado hasta ayer. Los periodos recientes pueden salir más bajos: aún no han tenido tiempo de registrar la primera.';

const CHOICE_NOTE =
    'Personas que pulsaron captura secreta o pública en esa pantalla. Si alguien pulsa las dos, cuenta la última. Misma agrupación (día, semana o mes) y mismo corte: hasta ayer, hora de Madrid.';

interface FirstCaptureConversionChartProps {
    days: number | null;
    isMobile?: boolean;
}

function formatPeriodLabel(iso: string, granularity: IncomeGranularity, withYear = false): string {
    const [year, month, day] = iso.slice(0, 10).split('-');
    if (!year || !month || !day) return iso;

    if (granularity === 'month') {
        const d = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
        return d.toLocaleDateString('es-ES', {
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC',
        });
    }

    if (granularity === 'week') {
        return withYear
            ? `Sem ${Number(day)}/${Number(month)}/${year}`
            : `Sem ${Number(day)}/${Number(month)}`;
    }

    return withYear ? `${Number(day)}/${Number(month)}/${year}` : `${Number(day)}/${Number(month)}`;
}

function formatPct(value: number, digits = 1): string {
    return `${value.toLocaleString('es-ES', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })} %`;
}

function periodRate(rows: FirstCaptureConversionPoint[]): { pct: number; registered: number; firstCapture: number } {
    const registered = rows.reduce((acc, row) => acc + row.registered, 0);
    const firstCapture = rows.reduce((acc, row) => acc + row.firstCapture, 0);
    const pct = registered > 0 ? (100 * firstCapture) / registered : 0;
    return { pct, registered, firstCapture };
}

function choiceTotals(rows: FirstCaptureChoicePoint[]): { secret: number; publicUsers: number; total: number } {
    const secret = rows.reduce((acc, row) => acc + row.secretUsers, 0);
    const publicUsers = rows.reduce((acc, row) => acc + row.publicUsers, 0);
    return { secret, publicUsers, total: secret + publicUsers };
}

function sharePct(part: number, total: number): number {
    return total > 0 ? (100 * part) / total : 0;
}

export function FirstCaptureConversionChart({ days, isMobile = false }: FirstCaptureConversionChartProps) {
    const [granularity, setGranularity] = useState<IncomeGranularity>('week');
    const { data, isLoading, error } = useFirstCaptureConversion(days, granularity);
    const choice = useFirstCaptureChoice(days, granularity);

    const chartData = useMemo(
        () =>
            (data ?? []).map((row) => ({
                periodStart: row.periodStart,
                conversionPct: row.conversionPct,
                registered: row.registered,
                firstCapture: row.firstCapture,
            })),
        [data],
    );

    const summary = useMemo(() => periodRate(data ?? []), [data]);
    const choiceSummary = useMemo(() => choiceTotals(choice.data ?? []), [choice.data]);
    const choiceChartData = useMemo(
        () =>
            (choice.data ?? []).map((row) => ({
                periodStart: row.periodStart,
                secret: row.secretUsers,
                publicUsers: row.publicUsers,
            })),
        [choice.data],
    );

    return (
        <div className="space-y-3 min-w-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">Registro → primera captura</h3>
                <p className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {isLoading
                        ? '—'
                        : `${formatPct(summary.pct)} en el periodo · ${summary.firstCapture.toLocaleString('es-ES')} de ${summary.registered.toLocaleString('es-ES')}`}
                </p>
            </div>

            <div className="flex flex-wrap gap-1 w-full min-w-0">
                {GRANULARITY_OPTIONS.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => setGranularity(opt.value)}
                        className={cn(
                            'shrink-0 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                            granularity === opt.value
                                ? 'bg-muted text-foreground'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            <p className="text-[11px] text-muted-foreground">{NOTE}</p>

            <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0">
                <div className={isMobile ? 'h-[280px]' : 'h-[400px]'}>
                    {isLoading ? (
                        <div className="h-full flex items-center justify-center text-muted-foreground">
                            <RefreshCw size={20} className="animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="h-full flex items-center justify-center text-sm text-rose-500 px-4 text-center">
                            {(error as Error).message || 'No se pudo cargar la conversión a primera captura.'}
                        </div>
                    ) : chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{
                                    top: 8,
                                    right: isMobile ? 4 : 8,
                                    bottom: isMobile ? 8 : 4,
                                    left: isMobile ? 0 : -4,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                <XAxis
                                    dataKey="periodStart"
                                    tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                    tickLine={false}
                                    axisLine={false}
                                    minTickGap={isMobile ? 36 : 24}
                                    interval="preserveStartEnd"
                                    tickFormatter={(val: string) =>
                                        formatPeriodLabel(String(val ?? ''), granularity)
                                    }
                                />
                                <YAxis
                                    width={isMobile ? 36 : 44}
                                    tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                    tickLine={false}
                                    axisLine={false}
                                    domain={[0, 100]}
                                    ticks={[0, 25, 50, 75, 100]}
                                    tickFormatter={(value: number) => `${value}%`}
                                />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    labelStyle={{ color: 'hsl(210 40% 98%)' }}
                                    labelFormatter={(val) =>
                                        formatPeriodLabel(String(val ?? ''), granularity, true)
                                    }
                                    formatter={(value: number, _name, item) => {
                                        const row = item?.payload as {
                                            firstCapture?: number;
                                            registered?: number;
                                        } | undefined;
                                        const first = row?.firstCapture ?? 0;
                                        const registered = row?.registered ?? 0;
                                        return [
                                            `${formatPct(Number(value) || 0)} · ${first.toLocaleString('es-ES')} de ${registered.toLocaleString('es-ES')}`,
                                            '1ª captura',
                                        ];
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="conversionPct"
                                    stroke={LINE_COLOR}
                                    strokeWidth={2}
                                    dot={chartData.length < 24}
                                    name="1ª captura"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                            No hay registros en el rango seleccionado
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap items-baseline justify-between gap-2 pt-2">
                <h3 className="text-sm font-semibold text-foreground">Secreta o pública</h3>
                <p className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {choice.isLoading
                        ? '—'
                        : choice.error
                            ? 'No disponible'
                            : choiceSummary.total === 0
                                ? 'Sin elecciones en el periodo'
                                : `${formatPct(sharePct(choiceSummary.secret, choiceSummary.total))} secreta · ${formatPct(sharePct(choiceSummary.publicUsers, choiceSummary.total))} pública`}
                </p>
            </div>

            <div className="grid grid-cols-2 gap-3 min-w-0">
                <div className="rounded-xl border border-border bg-card p-4 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Captura secreta</p>
                    <p className="text-2xl font-bold tabular-nums" style={{ color: SECRET_COLOR }}>
                        {choice.isLoading || choice.error ? '—' : choiceSummary.secret.toLocaleString('es-ES')}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1 tabular-nums">
                        {choice.isLoading || choice.error ? '—' : formatPct(sharePct(choiceSummary.secret, choiceSummary.total))}
                    </p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Captura pública</p>
                    <p className="text-2xl font-bold tabular-nums" style={{ color: PUBLIC_COLOR }}>
                        {choice.isLoading || choice.error ? '—' : choiceSummary.publicUsers.toLocaleString('es-ES')}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1 tabular-nums">
                        {choice.isLoading || choice.error ? '—' : formatPct(sharePct(choiceSummary.publicUsers, choiceSummary.total))}
                    </p>
                </div>
            </div>

            <p className="text-[11px] text-muted-foreground">{CHOICE_NOTE}</p>

            <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0">
                <div className={isMobile ? 'h-[280px]' : 'h-[320px]'}>
                    {choice.isLoading ? (
                        <div className="h-full flex items-center justify-center text-muted-foreground">
                            <RefreshCw size={20} className="animate-spin" />
                        </div>
                    ) : choice.error ? (
                        <div className="h-full flex items-center justify-center text-sm text-rose-500 px-4 text-center">
                            {(choice.error as Error).message || 'No se pudo cargar la elección de la primera captura.'}
                        </div>
                    ) : choiceChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={choiceChartData}
                                margin={{
                                    top: 8,
                                    right: isMobile ? 4 : 8,
                                    bottom: isMobile ? 8 : 4,
                                    left: isMobile ? 0 : -4,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                <XAxis
                                    dataKey="periodStart"
                                    tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                    tickLine={false}
                                    axisLine={false}
                                    minTickGap={isMobile ? 36 : 24}
                                    interval="preserveStartEnd"
                                    tickFormatter={(val: string) =>
                                        formatPeriodLabel(String(val ?? ''), granularity)
                                    }
                                />
                                <YAxis
                                    width={isMobile ? 28 : 36}
                                    allowDecimals={false}
                                    tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    labelStyle={{ color: 'hsl(210 40% 98%)' }}
                                    labelFormatter={(val) =>
                                        formatPeriodLabel(String(val ?? ''), granularity, true)
                                    }
                                    formatter={(value: number, name: string, item) => {
                                        const row = item?.payload as {
                                            secret?: number;
                                            publicUsers?: number;
                                        } | undefined;
                                        const total = (row?.secret ?? 0) + (row?.publicUsers ?? 0);
                                        return [
                                            `${Number(value || 0).toLocaleString('es-ES')} · ${formatPct(sharePct(Number(value) || 0, total))}`,
                                            name,
                                        ];
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="secret" name="Secreta" stackId="choice" fill={SECRET_COLOR} />
                                <Bar
                                    dataKey="publicUsers"
                                    name="Pública"
                                    stackId="choice"
                                    fill={PUBLIC_COLOR}
                                    radius={[3, 3, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                            No hay elecciones en el rango seleccionado
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
